import React, { useRef, useEffect } from 'react';
import { 
  Bot, 
  Send, 
  Package, 
  MessageCircle,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import { 
  Sheet, 
  SheetContent, 
  SheetTitle, 
  SheetTrigger 
} from '@/components/shared/ui/sheet';
import { Button } from '@/components/shared/ui/button';
import { Input } from '@/components/shared/ui/input';
import { ScrollArea } from '@/components/shared/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import { useStorefrontAi } from '../../hooks/useStorefrontAi';

export function SmartAssistantSheet() {
  const { 
    messages, 
    isLoading, 
    isEnabled, 
    isInitializing,
    sendMessage,
  } = useStorefrontAi();
  
  const [isOpen, setIsOpen] = React.useState(false);
  const [input, setInput] = React.useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const content = input;
    setInput('');
    await sendMessage(content);
  };

  if (isInitializing || !isEnabled) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button 
            size="lg" 
            className="rounded-full w-14 h-14 shadow-2xl bg-indigo-600 hover:bg-indigo-700 hover:scale-105 transition-all duration-300 relative group"
          >
            <MessageCircle className="w-6 h-6 text-white group-hover:rotate-12 transition-transform" />
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-indigo-500 border-2 border-white"></span>
            </span>
          </Button>
        </SheetTrigger>
        
        <SheetContent side="right" className="sm:max-w-[440px] p-0 flex flex-col border-l-0 shadow-[-10px_0_30px_rgba(0,0,0,0.1)]">
          {/* Custom Header with Glassmorphism */}
          <div className="bg-indigo-600/90 backdrop-blur-md text-white p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-2xl backdrop-blur-sm border border-white/20 shadow-inner">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
                  Mafer AI
                  <Sparkles className="w-4 h-4 text-amber-300 fill-amber-300" />
                </SheetTitle>
                <p className="text-indigo-100/80 text-xs flex items-center gap-1.5 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                  En línea ahora
                </p>
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <ScrollArea className="flex-1 p-5 bg-slate-50/50">
            <div className="space-y-6" ref={scrollRef}>
              <AnimatePresence initial={false}>
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm
                      ${msg.role === 'user' ? 'bg-slate-200 text-slate-600' : 'bg-indigo-100 text-indigo-700'}`}>
                      {msg.role === 'user' ? 'Yo' : <Bot className="w-4 h-4" />}
                    </div>
                    
                    <div className={`p-4 rounded-2xl max-w-[85%] shadow-sm text-sm leading-relaxed
                      ${msg.role === 'user' 
                        ? 'bg-indigo-600 text-white rounded-tr-none' 
                        : 'bg-white border text-slate-700 rounded-tl-none border-slate-200'}`}>
                      {msg.content}
                      
                      {msg.isRich && msg.type === 'product_recommendation' && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100 flex gap-3 group cursor-pointer hover:border-indigo-200 transition-colors">
                          <div className="w-16 h-16 bg-slate-200 rounded-lg shrink-0 overflow-hidden">
                             <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-slate-200 flex items-center justify-center">
                               <Package className="w-6 h-6 text-indigo-300" />
                             </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-900 truncate">Vestido Floral Premium</p>
                            <p className="text-indigo-600 font-bold">$120.00</p>
                            <div className="flex items-center text-[10px] text-indigo-500 font-bold uppercase tracking-wider mt-1">
                              Ver Detalle <ChevronRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-3"
                >
                  <div className="shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-white border border-slate-200 p-4 rounded-2xl rounded-tl-none shadow-sm flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></span>
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                    <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>

          {/* Footer / Input */}
          <div className="p-4 border-t bg-white relative">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex items-center gap-2 p-1 border border-slate-200 rounded-2xl focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-400 transition-all bg-slate-50/50"
            >
              <Input 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="border-none bg-transparent shadow-none focus-visible:ring-0 text-sm"
              />
              <Button 
                type="submit" 
                size="icon" 
                disabled={!input.trim() || isLoading}
                className="rounded-xl shrink-0 bg-indigo-600 hover:bg-indigo-700"
              >
                <Send className="w-4 h-4 text-white" />
              </Button>
            </form>
            <p className="text-[9px] text-slate-400 text-center mt-3 uppercase tracking-widest font-medium">
              Powered by Mafer RAG System • Match 2025
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
